// hooks/useAdmin.js
import { useState, useEffect } from 'react';

/**
 * Hook para manejar listados con CRUD básico
 * Reduce duplicación en componentes admin
 */
export function useAdminList(fetchFunction) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchFunction();

      if (response?.error) {
        setError(response.error);
        setItems([]);
        return;
      }

      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];

      setItems(items);
    } catch (err) {
      setError(err.message || "Error al cargar datos");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 👈 SOLO una vez al montar

  return { items, loading, error, reload: cargarDatos };
}

/**
 * Hook para formularios con validación básica
 */
export function useForm(initialValues = {}, validationRules = {}) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    // Limpiar error cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validar campo al perder foco
    if (validationRules[name]) {
      const error = validationRules[name](values[name]);
      if (error) {
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    Object.keys(validationRules).forEach(field => {
      const error = validationRules[field](values[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const reset = (newValues = initialValues) => {
    setValues(newValues);
    setErrors({});
    setTouched({});
  };

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validate,
    reset,
    setValues
  };
}

/**
 * Hook para manejar operaciones CRUD completas
 */
export function useAdminCRUD(config) {
  const { 
    fetchAll, 
    create, 
    update, 
    remove 
  } = config;

  const { items, loading, error, reload } = useAdminList(fetchAll);
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async (data) => {
    try {
      setSubmitting(true);
      const result = await create( data);
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      await reload();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      setSubmitting(true);
      const result = await update( id, data);
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      await reload();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setSubmitting(true);
      const result = await remove(id);
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      await reload();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setSubmitting(false);
    }
  };

  return {
    items,
    loading,
    error,
    submitting,
    reload,
    create: handleCreate,
    update: handleUpdate,
    remove: handleDelete
  };
}

/**
 * Hook para manejar el tenant actual
 */
export function useTenant() {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTenant = async () => {
    try {
      setLoading(true);
      const { getTenantConfig } = await import('../services/api');
      const config = await getTenantConfig();
      setTenantConfig(config);
      setError(null);
    } catch (err) {
      setError(err.message);
      setTenantConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenant();
  }, []);

  return { tenantConfig, loading, error, reload: loadTenant };
}

/**
 * Hook para manejar modales/formularios
 */
export function useModal(initialState = false) {
  const [isOpen, setIsOpen] = useState(initialState);
  const [data, setData] = useState(null);

  const open = (initialData = null) => {
    setData(initialData);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setData(null);
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  return {
    isOpen,
    data,
    open,
    close,
    toggle
  };
}

/**
 * Hook para manejar confirmaciones
 */
export function useConfirm() {
  const confirm = (message) => {
    return window.confirm(message);
  };

  const confirmAsync = (message) => {
    return Promise.resolve(window.confirm(message));
  };

  return { confirm, confirmAsync };
}

/**
 * Hook para debounce (útil en búsquedas)
 */
export function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}